import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, Volume2, VolumeX, Instagram } from "lucide-react";
import Navigation from "@/components/Navigation";
import ChatbotWidget from "@/components/ChatbotWidget";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO = "/assets/logo.png";
const HERO_VIDEO = "/assets/hero.mp4";
const IMG_CONCEPT = "/assets/concept.png";

/* ===== Ambient audio — reverted to previous softer premium cabin loop (Mixkit 1588) ===== */
const AMBIENT_AUDIO = "https://assets.mixkit.co/active_storage/sfx/1588/1588-preview.mp3";

/* ===== Imagery (high-res, landscape, luxury) ===== */
const IMG_DINING = "/images/dining-wine.jpg";
const IMG_RED_CARPET = "/assets/red-carpet.png";
const IMG_PERFORMANCE = "/assets/performance.png";
const IMG_CUSTOM = "/images/custom-experience.jpg";
const IMG_CHAUFFEUR = "/images/chauffeur.jpg";
const IMG_HOTEL_POOL = "https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=2800&q=85";
const IMG_GIFT = "/images/gift.jpg";
const IMG_SOCIALS = "/images/socials-performance.jpg";
const IMG_ENTERTAINMENT = "/images/entertainment-audience.jpg";
const IMG_ASSISTANT = "/images/personal-assistant.jpg";
const IMG_LAUNCH = "/images/launch-event.jpg";
const IMG_AI_TECH = "/images/ai-technology.jpg";
const LOUNGE_VIDEO = "/videos/lounge.mp4";

/* ===== Destinations ===== */
const DESTINATIONS = [
  {
    name: "Qatar",
    tagline: "Arabian refinement, horizon-bound luxury",
    description: "A capital of prestige and modern grandeur — where five-star service, private estates and cultural icons converge on the Arabian Gulf.",
    image: "/images/qatar.jpg",
    mobilePos: "center 35%",
  },
  {
    name: "Saudi Arabia",
    tagline: "Desert kingdoms, modern majesty",
    description: "From the architectural marvels of Riyadh to the ancient wonders of AlUla, an experience that pairs heritage with the most exclusive new luxury addresses on earth.",
    image: "/images/saudi-arabia.jpg",
    mobilePos: "center 40%",
  },
  {
    name: "Greece",
    tagline: "Aegean light, timeless elegance",
    description: "Whitewashed cliffs, private yachts and the slow golden hours of the Aegean — a destination defined by light, legacy and refined simplicity.",
    image: "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=2800&q=85",
    mobilePos: "center 45%",
  },
  {
    name: "New York City",
    tagline: "Skyline energy, world-class nights",
    description: "The capital of culture. Penthouses above the skyline, private dining rooms, and access to the most coveted corners of Manhattan.",
    image: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=2800&q=85",
    mobilePos: "center 40%",
  },
  {
    name: "Oman",
    tagline: "Coastal fortresses, timeless coastline",
    description: "Where the Gulf of Oman meets golden mountains — historic forts, white-washed palaces and quiet bays offering a refined alternative to the headline cities of the region.",
    image: "/images/oman.jpg",
    mobilePos: "center 45%",
  },
  {
    name: "Abu Dhabi",
    tagline: "Island luxury, turquoise calm",
    description: "Private island resorts, ocean-front infinity pools and palm-lined promenades — Abu Dhabi pairs effortless luxury with a slower, more discreet rhythm than its neighbours.",
    image: "/images/abu-dhabi.jpg",
    mobilePos: "center 45%",
  },
];

/* ===== Reveal hook ===== */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("revealed");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ===== Animated Header — dramatic slide-in + optional continuous marquee loop ===== */
function SlideHeader({ overline, title, align = "left", light = true, testId, marquee = true }) {
  const ref = useReveal();
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  const color = light ? "text-white" : "text-zinc-900";
  return (
    <div className={`mb-10 sm:mb-14 ${alignCls}`} data-testid={testId}>
      {overline && (
        <p className="overline text-[#007AFF] mb-5 drift">{overline}</p>
      )}
      <h2
        ref={ref}
        className={`header-slide font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight ${color} leading-[1.08]`}
      >
        {title}
      </h2>
      {marquee && <MarqueeTitle title={title} />}
    </div>
  );
}

/* Continuous right-to-left marquee used underneath section headers */
function MarqueeTitle({ title }) {
  const word = `${title}`;
  const sep = "  ✦  ";
  const line = Array.from({ length: 6 }).map(() => word).join(sep);
  return (
    <div className="marquee-wrap mt-8" aria-hidden="true">
      <div className="marquee-track font-heading font-light text-white/20 text-3xl sm:text-4xl tracking-[0.15em] uppercase">
        <span>{line}{sep}</span>
        <span>{line}{sep}</span>
      </div>
    </div>
  );
}

/* ===== Subtle spacing divider that reveals the star background ===== */
function SectionDivider({ size = "sm" }) {
  const h = size === "lg" ? "h-16 sm:h-24" : "h-8 sm:h-14";
  return <div className={`section-divider ${h}`} aria-hidden="true" />;
}

/* ===== Buttons ===== */
function PillButton({ href = "#access", children, variant = "primary", testId, onClick }) {
  const cls = variant === "primary" ? "btn-pill btn-pill-primary" : "btn-pill btn-pill-ghost";
  return (
    <a href={href} onClick={onClick} className={cls} data-testid={testId}>
      {children}
    </a>
  );
}

/* ===== Ambient Audio Toggle ===== */
function AudioToggle() {
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = true;
    a.volume = 0.35;
    a.play().catch(() => {});
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (muted) {
      a.muted = false;
      a.volume = 0.35;
      a.play().catch(() => {});
      setMuted(false);
    } else {
      a.muted = true;
      setMuted(true);
    }
  };

  return (
    <>
      <audio ref={audioRef} src={AMBIENT_AUDIO} loop autoPlay muted preload="auto" data-testid="ambient-audio" />
      <button
        type="button"
        onClick={toggle}
        className="audio-toggle"
        aria-label={muted ? "Enable sound" : "Mute sound"}
        data-testid="audio-toggle-btn"
      >
        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </button>
    </>
  );
}

/* ===== SECTION 1: HERO (only text line updated per user directive) ===== */
function HeroSection() {
  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video">
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 hero-gradient" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <p className="overline text-white mb-6 text-shadow-strong font-medium">Music Without Borders</p>
        <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl text-white tracking-tighter font-light mb-5 text-shadow-strong">
          AirStage<span className="text-[#007AFF]">X</span>
        </h1>
        <p className="font-heading text-xl sm:text-2xl text-white tracking-wide mb-5 text-shadow-strong font-light">
          Music. Travel. Access.
        </p>
        <p className="max-w-xl text-white text-base sm:text-lg mb-2 leading-relaxed text-shadow-strong">
          Exclusive concerts in the sky.
        </p>
        <p className="max-w-xl text-white text-base sm:text-lg mb-2 text-shadow-strong">
          A rare, intimate experience with world-class artists.
        </p>
        <p className="text-white text-sm sm:text-base mb-12 tracking-wider text-shadow-strong">
          Limited seats. Selected guests only.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a href="#access" className="btn-pill btn-pill-primary" data-testid="hero-join-waitlist-btn">Invitation Only</a>
          <a href="#experience" className="btn-pill btn-pill-ghost" data-testid="hero-view-experience-btn">View Experience</a>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 scroll-indicator z-10">
        <ChevronDown className="w-5 h-5 text-zinc-400" />
      </div>
    </section>
  );
}

/* ===== SECTION 2: CONCEPT ===== */
function ConceptSection() {
  const ref = useReveal();
  return (
    <section id="concept" className="py-24 sm:py-32" data-testid="concept-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="overline text-[#007AFF] mb-6">The Concept</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-white mb-8">
              A new way to<br />experience music
            </h2>
            <div className="space-y-4 text-zinc-200 text-base leading-relaxed">
              <p>AirStageX turns the journey into the main event.</p>
              <p>A private plane becomes a live performance space.</p>
              <p>You travel with the artist, not to them.</p>
            </div>
            <div className="mt-8 pt-8 border-t border-white/10 space-y-2">
              <p className="text-zinc-300 text-sm">No large crowds.</p>
              <p className="text-zinc-300 text-sm">No distance.</p>
              <p className="text-zinc-300 text-sm">A controlled, private setting.</p>
            </div>
          </div>
          <div className="img-zoom">
            <img src={IMG_CONCEPT} alt="AirStageX Interior" className="w-full h-[320px] sm:h-[620px] object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* =============================================================
   LAYOUT COMPONENTS
   ============================================================= */

/* Full-bleed cinematic image (or video) with text overlay */
function FullBleedSection({ id, overline, title, paragraphs = [], image, video, overlay = "left", button, testId, height = "h-[90vh]", mobilePos }) {
  const textRef = useReveal();
  const overlayCls =
    overlay === "left"
      ? "cinematic-overlay-left items-center justify-start text-left"
      : overlay === "right"
      ? "cinematic-overlay-right items-center justify-end text-left"
      : "cinematic-overlay-bottom items-end justify-start text-left";
  const textPos = overlay === "right" ? "lg:mr-0 lg:ml-auto" : "";
  const mediaStyle = mobilePos ? { "--mobile-obj-pos": mobilePos } : undefined;
  return (
    <section id={id} className={`relative w-full ${height} overflow-hidden`} data-testid={testId}>
      {video ? (
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover media-mobile-pos"
          style={mediaStyle}
        >
          <source src={video} type="video/mp4" />
        </video>
      ) : (
        <img
          src={image}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover media-mobile-pos"
          style={mediaStyle}
        />
      )}
      <div className={`absolute inset-0 flex ${overlayCls} p-5 sm:p-16 lg:p-24`}>
        <div ref={textRef} className={`reveal w-full max-w-xl ${textPos}`}>
          {overline && <p className="overline text-[#007AFF] mb-5 drift text-shadow-strong">{overline}</p>}
          <h2 className="header-slide font-heading font-light text-white text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] mb-5 sm:mb-7 text-shadow-strong break-words">
            {title}
          </h2>
          <MarqueeTitle title={title} />
          <div className="space-y-4 text-white text-sm sm:text-base leading-relaxed text-shadow-strong">
            {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          {button && (
            <div className="mt-7 sm:mt-9">
              <PillButton href={button.href} variant={button.variant || "primary"} testId={button.testId}>
                {button.label}
              </PillButton>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* Big split — image (or video) dominant, text on side */
function BigSplitSection({ id, overline, title, paragraphs = [], image, video, imageSide = "right", button, testId, mobilePos }) {
  const textRef = useReveal();
  const mediaStyle = mobilePos ? { "--mobile-obj-pos": mobilePos } : undefined;
  return (
    <section id={id} className="py-14 sm:py-28 border-t border-white/5" data-testid={testId}>
      <div className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${imageSide === "left" ? "lg:[&>*:first-child]:order-1 lg:[&>*:last-child]:order-2" : ""}`}>
          <div className={`img-zoom ${imageSide === "left" ? "lg:col-span-8 lg:col-start-1" : "lg:col-span-8 lg:col-start-5"}`}>
            {video ? (
              <video
                autoPlay muted loop playsInline
                className="w-full h-[280px] sm:h-[640px] lg:h-[720px] object-cover media-mobile-pos"
                style={mediaStyle}
              >
                <source src={video} type="video/mp4" />
              </video>
            ) : (
              <img
                src={image}
                alt={title}
                className="w-full h-[280px] sm:h-[640px] lg:h-[720px] object-cover media-mobile-pos"
                style={mediaStyle}
              />
            )}
          </div>
          <div
            ref={textRef}
            className={`reveal ${imageSide === "left" ? "lg:col-span-4 lg:col-start-9 lg:row-start-1" : "lg:col-span-4 lg:col-start-1 lg:row-start-1"}`}
          >
            {overline && <p className="overline text-[#007AFF] mb-5 drift">{overline}</p>}
            <h2 className="header-slide font-heading font-light text-3xl sm:text-5xl lg:text-[3.25rem] tracking-tight text-white leading-[1.1] mb-5 sm:mb-7 break-words">
              {title}
            </h2>
            <MarqueeTitle title={title} />
            <div className="space-y-4 text-zinc-200 text-sm sm:text-[15px] leading-relaxed">
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {button && (
              <div className="mt-7 sm:mt-9">
                <PillButton href={button.href} variant={button.variant || "primary"} testId={button.testId}>
                  {button.label}
                </PillButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== LAUNCH EVENT (full-bleed cinematic) ===== */
function LaunchEventSection() {
  return (
    <FullBleedSection
      id="launch"
      overline="Autumn 2026"
      title="Air Stage X Launch"
      paragraphs={[
        "Air Stage X will launch in Autumn 2026 with an exclusive VIP gala dinner, introducing a new concept in luxury entertainment.",
        "The evening will feature a live performance by a globally recognised A-list artist, with an invited audience of celebrities, media, influencers, and select guests.",
        "The event will be professionally live streamed to a global audience, designed to generate strong international attention and position Air Stage X as a high profile, culture shaping experience from launch.",
      ]}
      image={IMG_LAUNCH}
      overlay="left"
      button={{ label: "Read More", href: "#experience", variant: "primary", testId: "launch-read-more-btn" }}
      testId="launch-section"
    />
  );
}

/* ===== THE EXPERIENCE ===== */
function ExperienceSection() {
  return (
    <section id="experience" className="border-t border-white/5" data-testid="experience-section">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-12 pt-24 sm:pt-32 pb-8">
        <SlideHeader overline="From Arrival to Return" title="The Experience" testId="experience-header" />
        <div className="max-w-3xl text-zinc-200 text-base sm:text-lg leading-relaxed space-y-5">
          <p>From Departure at Heathrow, your journey begins in a private VIP lounge with discreet service and priority boarding.</p>
          <p>Onboard, guests are served refined dining with champagne and curated canapés. A live performance by a global A-list artist takes place in flight, creating an intimate setting. Each guest receives a limited edition gift pouch and access to a meet and greet with the artist.</p>
          <p>On arrival, a private chauffeur transfers guests to a luxury hotel for a four night stay with breakfast included.</p>
          <p>The return is arranged in full, with the same level of care from departure to arrival back in London.</p>
        </div>
      </div>

      <BigSplitSection
        id="experience-lounge"
        overline="01 — Private Lounge"
        title="A discreet arrival at Heathrow"
        paragraphs={[
          "Your journey opens with access to a private VIP lounge, where a dedicated team welcomes you with champagne, refreshments and quiet service.",
          "A calm, staffed setting designed so every moment before boarding feels considered and unhurried.",
        ]}
        video={LOUNGE_VIDEO}
        imageSide="right"
        button={{ label: "Enquire Now", href: "#access", variant: "primary", testId: "exp-lounge-inquire-btn" }}
        testId="exp-lounge-row"
      />

      <FullBleedSection
        id="experience-boarding"
        overline="02 — Priority Boarding"
        title="Red carpet to the aircraft"
        paragraphs={[
          "Boarding is conducted via a private red carpet directly to the aircraft, with every detail orchestrated by a personal ground team.",
          "No queues. No interruptions. Only a composed, cinematic transition to the cabin.",
        ]}
        image={IMG_RED_CARPET}
        overlay="right"
        button={{ label: "Request a Quote", href: "#access", variant: "primary", testId: "exp-boarding-quote-btn" }}
        testId="exp-boarding-row"
        height="h-[60vh] md:h-[85vh]"
      />

      <BigSplitSection
        id="experience-dining"
        overline="03 — Fine Dining"
        title="Refined dining at 40,000 feet"
        paragraphs={[
          "Onboard, guests are served a bespoke menu with selection of handcrafted canapés, premium champagne and wines chosen by an onboard sommelier.",
          "Each course is prepared with high-quality ingredients and presented with the precision of a fine dining room — in the sky.",
        ]}
        image={IMG_DINING}
        imageSide="left"
        button={{ label: "Read More", href: "#assistant", variant: "ghost", testId: "exp-dining-read-btn" }}
        testId="exp-dining-row"
      />

      <FullBleedSection
        id="experience-performance"
        overline="04 — Live Performance"
        title="A global A-list set, in flight"
        paragraphs={[
          "A live performance by a world-renowned A-list artist takes place mid-flight, at 40,000 feet, with the cabin set with bespoke lighting and sound.",
          "Each guest receives studio-quality headphones for an immersive, intimate delivery unlike any venue on the ground.",
        ]}
        image={IMG_PERFORMANCE}
        overlay="left"
        button={{ label: "Enquire Now", href: "#access", variant: "primary", testId: "exp-performance-inquire-btn" }}
        testId="exp-performance-row"
        height="h-[60vh] md:h-[90vh]"
      />

      {/* subtle gap revealing the star background */}
      <SectionDivider size="sm" />

      <FullBleedSection
        id="experience-custom"
        overline="05 — Custom Experiences"
        title="Tailored, Without Limits"
        paragraphs={[
          "Every journey can be tailored — from your choice of artist and destination, to private groups, anniversaries and once-in-a-lifetime occasions.",
          "A bespoke layer of colour, craftsmanship and detail brought to every element of the experience.",
        ]}
        image={IMG_CUSTOM}
        overlay="right"
        button={{ label: "Request a Quote", href: "#access", variant: "primary", testId: "exp-custom-quote-btn" }}
        testId="exp-custom-row"
        height="h-[60vh] md:h-[95vh]"
      />
    </section>
  );
}

/* ===== DESTINATIONS & EXPERIENCE (intro copy, cinematic) ===== */
function DestinationsIntroSection() {
  return (
    <FullBleedSection
      id="destinations-intro"
      overline="Curated Journeys"
      title="Destinations & Experience"
      paragraphs={[
        "Air Stage X connects a tailored selection of the world’s most iconic destinations, including Qatar, Saudi Arabia, Greece, New York City, Oman, and Abu Dhabi, each chosen for its cultural prestige and luxury offering.",
        "From arrival, every detail is managed. Guests are met by a private chauffeur and transferred to a five-star hotel for a four-night stay, with a champagne welcome on arrival.",
        "A dedicated hair stylist and professional makeup artist travel on each flight, available on request throughout the journey.",
        "After the stay, guests return to Heathrow, where a personal concierge ensures a smooth and well-managed conclusion to the experience.",
      ]}
      image={IMG_CHAUFFEUR}
      overlay="right"
      testId="destinations-intro-section"
      height="h-[60vh] md:h-[90vh]"
    />
  );
}

/* ===== DESTINATIONS — each one its own full cinematic section ===== */
function DestinationSection({ dest, index }) {
  const overlay = index % 2 === 0 ? "left" : "right";
  return (
    <FullBleedSection
      id={`destination-${dest.name.toLowerCase().replace(/\s+/g, "-")}`}
      overline={`Destination 0${index + 1}`}
      title={dest.name}
      paragraphs={[dest.tagline, dest.description]}
      image={dest.image}
      overlay={overlay}
      mobilePos={dest.mobilePos}
      button={{ label: "Request a Quote", href: "#access", variant: "primary", testId: `dest-${dest.name.toLowerCase().replace(/\s+/g, "-")}-quote-btn` }}
      testId={`destination-${dest.name.toLowerCase().replace(/\s+/g, "-")}-section`}
      height="h-[55vh] md:h-[92vh]"
    />
  );
}

function DestinationsGroup() {
  return (
    <section id="destinations" className="border-t border-white/5" data-testid="destinations-section">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-12 pt-24 sm:pt-32 pb-4">
        <SlideHeader overline="Where We Fly" title="The Destinations" align="center" testId="destinations-header" />
      </div>
      {DESTINATIONS.map((dest, i) => (
        <DestinationSection key={dest.name} dest={dest} index={i} />
      ))}
    </section>
  );
}

/* ===== THE DESTINATION (hotels) ===== */
function TheDestinationSection() {
  return (
    <FullBleedSection
      id="destination"
      overline="The Stay"
      title="The Destination"
      paragraphs={[
        "Each Air Stage X destination is paired with a selection of five star hotels, chosen for their setting, service, and overall standard.",
        "Properties include access to spas, pools, fitness centres, and 24 hour dining, with dedicated staff ensuring a smooth and personalised stay.",
        "Whether marking a special occasion or taking time away, the destination experience is designed for comfort, privacy, and ease.",
      ]}
      image={IMG_HOTEL_POOL}
      overlay="left"
      button={{ label: "Read More", href: "#socials", variant: "primary", testId: "destination-read-btn" }}
      testId="destination-section"
      height="h-[60vh] md:h-[95vh]"
    />
  );
}

/* ===== THE ENTERTAINMENT ===== */
function EntertainmentSection() {
  return (
    <FullBleedSection
      id="entertainment"
      overline="The Main Event"
      title="The Entertainment"
      paragraphs={[
        "At the centre of Air Stage X is a live performance by world-renowned A-list artists, performing with their band at 40,000 feet.",
        "Guests can select their destination, travel date, and preferred artist, creating a personalised experience. Each guest is provided with high-quality headphones for clear, immersive sound throughout the performance.",
        "The cabin is set with lighting designed to match the performance, alongside champagne and fine dining. The performance is also live streamed globally.",
        "During the stay, guests are invited to a private meet and greet with the artist and their band, including a photo opportunity.",
        "Air Stage X brings together music, travel, and live performance in one setting.",
      ]}
      image={IMG_ENTERTAINMENT}
      overlay="right"
      button={{ label: "Enquire Now", href: "#access", variant: "primary", testId: "entertainment-inquire-btn" }}
      testId="entertainment-section"
      height="h-[95vh]"
    />
  );
}

/* ===== THE DINING ===== */
function DiningSection() {
  return (
    <BigSplitSection
      id="dining"
      overline="At 40,000 Feet"
      title="The Dining"
      paragraphs={[
        "A three course fine dining experience is served onboard at 40,000 feet, beginning with handcrafted canapés and premium champagne.",
        "Each course is prepared using high quality ingredients, with a focus on flavour and presentation. An onboard sommelier guides guests through a curated selection of wines, spirits, and champagnes.",
        "Menus are tailored to all dietary requirements, including vegan, vegetarian, and specific intolerances.",
      ]}
      image={IMG_DINING}
      imageSide="right"
      button={{ label: "Read More", href: "#gift", variant: "ghost", testId: "dining-read-btn" }}
      testId="dining-section"
    />
  );
}

/* ===== THE SOCIALS ===== */
function SocialsSection() {
  return (
    <BigSplitSection
      id="socials"
      overline="Share the Journey"
      title="The Socials"
      paragraphs={[
        "Each Air Stage X performance is professionally live streamed, giving global audiences access to the experience.",
        "Guests receive a personalised social media pack with key handles, tags, and content highlights to share their journey.",
        "For those who prefer privacy, anonymity options are available, allowing the experience to remain as private or as visible as desired.",
      ]}
      image={IMG_SOCIALS}
      imageSide="left"
      button={{ label: "Read More", href: "#gift", variant: "ghost", testId: "socials-read-btn" }}
      testId="socials-section"
    />
  );
}

/* ===== THE GIFT ===== */
function GiftSection() {
  return (
    <BigSplitSection
      id="gift"
      overline="A Keepsake"
      title="The Gift"
      paragraphs={[
        "Each guest receives a limited edition velvet pouch as a keepsake of the experience.",
        "Inside are signed items from the artist and Air Stage X, along with a gold plated boarding pass created as a commemorative piece.",
        "The pouch also includes selected items from luxury partners, chosen to complement the journey.",
      ]}
      image={IMG_GIFT}
      imageSide="right"
      button={{ label: "Read More", href: "#assistant", variant: "ghost", testId: "gift-read-btn" }}
      testId="gift-section"
    />
  );
}

/* ===== YOUR PERSONAL ASSISTANT ===== */
function PersonalAssistantSection() {
  return (
    <BigSplitSection
      id="assistant"
      overline="Dedicated Service"
      title="Your Personal Assistant"
      paragraphs={[
        "Each Air Stage X guest is assigned a dedicated Personal Assistant to manage every aspect of the journey from booking to return.",
        "Your assistant handles travel arrangements, visas, itineraries, and special requests with precision and discretion, including last-minute changes and tailored experiences at your destination.",
        "Available before, during, and after your journey, your Personal Assistant ensures everything runs smoothly so you can focus on the experience.",
      ]}
      image={IMG_ASSISTANT}
      imageSide="left"
      button={{ label: "Enquire Now", href: "#access", variant: "primary", testId: "assistant-inquire-btn" }}
      testId="assistant-section"
    />
  );
}

/* ===== AI & TECHNOLOGY IN EVERY FLIGHT ===== */
function AITechnologySection() {
  const ref = useReveal();
  const features = [
    "Personalisation of lighting, music, temperature, meal preferences, and passenger profiles",
    "Voice-controlled cabin functions for entertainment, seat controls, and service requests",
    "High-speed connectivity through satellite internet",
    "Curated digital concierge features before, during, and after the flight",
    "Predictive maintenance and aircraft monitoring on the operator side",
    "Smart cabin automation tied to mood, itinerary, and guest preferences",
  ];
  return (
    <section id="ai-technology" className="py-14 sm:py-28 border-t border-white/5" data-testid="ai-technology-section">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="img-zoom lg:col-span-7 lg:col-start-1 bg-black">
            <img
              src={IMG_AI_TECH}
              alt="AI & Technology in Every Flight"
              className="w-full h-[320px] sm:h-[640px] lg:h-[720px] object-contain"
            />
          </div>
          <div ref={ref} className="reveal lg:col-span-5 lg:col-start-8">
            <p className="overline text-[#007AFF] mb-5 drift">Intelligent Cabin</p>
            <h2 className="header-slide font-heading font-light text-3xl sm:text-5xl lg:text-[3.25rem] tracking-tight text-white leading-[1.1] mb-5 sm:mb-7 break-words">
              AI &amp; Tech
            </h2>
            <MarqueeTitle title="AI & Tech" />
            <ul className="space-y-4 text-zinc-200 text-sm sm:text-[15px] leading-relaxed">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3" data-testid="ai-feature-item">
                  <span className="mt-2 w-1.5 h-1.5 bg-[#007AFF] flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 sm:mt-9">
              <PillButton href="#access" variant="primary" testId="ai-tech-enquire-btn">
                Enquire Now
              </PillButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== ARTISTS — headliners band + DJ column ===== */
const IMG_ARTIST_BAND = "/images/band-silhouette.jpg";
const IMG_ARTIST_DJ = "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1400&q=80";

function ArtistsSection() {
  const ref = useReveal();
  const artists = [
    {
      name: "Global headliners & live bands",
      origin: "Los Angeles · London · Nashville",
      desc: "From Grammy-winning headliners to signature live bands — vocals, guitars and drums — every performance is hand-picked to match the journey.",
      image: IMG_ARTIST_BAND,
    },
    {
      name: "DJs & Producers",
      origin: "Ibiza · Dubai · New York",
      desc: "A-list DJs and producers curating intimate, high-end audio experiences.",
      image: IMG_ARTIST_DJ,
    },
  ];

  return (
    <section id="artists" className="py-24 sm:py-32 border-t border-white/5" data-testid="artists-section">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-12">
        <div ref={ref} className="reveal mb-12 sm:mb-16">
          <p className="overline text-[#007AFF] mb-5 drift">The Performers</p>
          <h2 className="header-slide font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.08] mb-4">
            Artists
          </h2>
          <MarqueeTitle title="Artists" />
          <p className="text-zinc-200 text-base max-w-2xl leading-relaxed">
            World-class performers from every corner of the globe, curated for each journey.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
          {artists.map((artist) => (
            <div
              key={artist.name}
              className="group"
              data-testid={`artist-${artist.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="img-zoom overflow-hidden">
                <img
                  src={artist.image}
                  alt={artist.name}
                  className="w-full h-[320px] sm:h-[520px] object-cover"
                />
              </div>
              <div className="mt-6">
                <p className="overline text-[#007AFF] mb-2">{artist.origin}</p>
                <h3 className="font-heading text-2xl sm:text-3xl font-light text-white mb-3 tracking-wide">{artist.name}</h3>
                <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">{artist.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <PillButton href="#access" variant="primary" testId="artists-inquire-btn">
            Enquire Now
          </PillButton>
        </div>
      </div>
    </section>
  );
}

/* ===== ACCESS / WAITLIST ===== */
function AccessSection() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", location: "", spending_range: "" });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/waitlist`, form);
      setStatus("success");
      setMessage("You're on the list. We'll be in touch.");
      setForm({ name: "", email: "", location: "", spending_range: "" });
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="access" className="py-24 sm:py-32 border-t border-white/5" data-testid="access-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div ref={ref} className="reveal">
            <p className="overline text-[#007AFF] mb-6">Membership</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-white mb-8">Access</h2>
            <p className="text-zinc-100 text-lg mb-6">AirStageX is not open to everyone.</p>
            <p className="text-zinc-200 text-base mb-8 leading-relaxed">
              Access is granted through private invitations, waitlist approval, or membership.
            </p>
            <div className="space-y-4">
              {["Private invitations", "Waitlist approval", "Membership"].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-[#007AFF] flex-shrink-0" />
                  <p className="text-zinc-200 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12" data-testid="waitlist-success">
                <div className="w-12 h-12 border border-[#007AFF] flex items-center justify-center mb-6 rounded-full">
                  <Check className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="font-heading text-xl font-light text-white mb-2">Application Received</h3>
                <p className="text-zinc-300 text-sm">{message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="waitlist-form">
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Full Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="Your full name"
                    required
                    data-testid="waitlist-name-input"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="your@email.com"
                    required
                    data-testid="waitlist-email-input"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="City, Country"
                    data-testid="waitlist-location-input"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Annual Travel Budget</label>
                  <select
                    value={form.spending_range}
                    onChange={(e) => setForm({ ...form, spending_range: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors w-full text-sm appearance-none cursor-pointer"
                    data-testid="waitlist-spending-select"
                  >
                    <option value="" className="bg-[#0A0A0A]">Select range</option>
                    <option value="50k-100k" className="bg-[#0A0A0A]">$50,000 - $100,000</option>
                    <option value="100k-250k" className="bg-[#0A0A0A]">$100,000 - $250,000</option>
                    <option value="250k-500k" className="bg-[#0A0A0A]">$250,000 - $500,000</option>
                    <option value="500k+" className="bg-[#0A0A0A]">$500,000+</option>
                  </select>
                </div>
                {status === "error" && <p className="text-red-400 text-sm" data-testid="waitlist-error">{message}</p>}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-pill btn-pill-primary w-full disabled:opacity-50"
                  data-testid="waitlist-submit-btn"
                >
                  {status === "loading" ? "Submitting..." : "Invitation Only"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== FINAL CTA ===== */
function FinalCTASection() {
  const ref = useReveal();
  return (
    <section className="py-32 sm:py-40 border-t border-white/5" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
        <div ref={ref} className="reveal">
          <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-white mb-6">
            A new category<br />of experience
          </h2>
          <p className="text-zinc-100 text-base sm:text-lg mb-10">Join the waitlist.</p>
          <PillButton href="#access" variant="primary" testId="final-cta-join-waitlist-btn">
            Invitation Only
          </PillButton>
        </div>
      </div>
    </section>
  );
}

/* ===== FOOTER ===== */
function Footer() {
  return (
    <footer className="py-16 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <img src={LOGO} alt="AirStageX" className="h-12 mb-6" />
            <p className="text-zinc-300 text-sm leading-relaxed">
              Exclusive concerts in the sky.
            </p>
          </div>
          <div>
            <p className="overline text-zinc-100 mb-4">Navigation</p>
            <div className="space-y-3">
              <a href="#experience" className="block text-zinc-300 text-sm hover:text-white transition-colors">The Experience</a>
              <a href="#entertainment" className="block text-zinc-300 text-sm hover:text-white transition-colors">The Entertainment</a>
              <a href="#dining" className="block text-zinc-300 text-sm hover:text-white transition-colors">The Dining</a>
              <a href="#destinations" className="block text-zinc-300 text-sm hover:text-white transition-colors">The Destinations</a>
              <a href="#socials" className="block text-zinc-300 text-sm hover:text-white transition-colors">The Socials</a>
              <a href="#gift" className="block text-zinc-300 text-sm hover:text-white transition-colors">The Gift</a>
              <a href="#assistant" className="block text-zinc-300 text-sm hover:text-white transition-colors">The PA</a>
              <a href="/partnership" className="block text-zinc-300 text-sm hover:text-white transition-colors" data-testid="footer-partnership-link">Partnership</a>
            </div>
          </div>
          <div>
            <p className="overline text-zinc-100 mb-4">Contact</p>
                      
            <a href="mailto:contact@airstagex.com" className="text-white text-sm hover:text-[#007AFF] transition-colors block mb-3" data-testid="footer-email-contact">
              contact@airstagex.com
            </a>
            <a href="mailto:investments@airstagex.com" className="text-white text-sm hover:text-[#007AFF] transition-colors block mb-3" data-testid="footer-email-techsupport">
              investments@airstagex.com
            </a>
            <p className="text-zinc-300 text-sm">Location: London</p>
            <a
              href="https://www.instagram.com/airstagex?utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AirStageX on Instagram"
              className="inline-flex items-center gap-2 mt-5 text-zinc-300 text-sm hover:text-[#007AFF] transition-colors"
              data-testid="footer-instagram-link"
            >
              <Instagram className="w-5 h-5" />
              <span>Instagram</span>
            </a>
          </div>
        </div>
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-zinc-400 text-xs">All rights reserved &copy; 2025 AirStageX</p>
          <a href="/admin" className="text-zinc-500 text-xs hover:text-zinc-200 transition-colors">Admin</a>
        </div>
      </div>
    </footer>
  );
}

/* ===== MAIN PAGE ===== */
export default function HomePage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 60% 0px 60%" }
    );
    document.querySelectorAll(".reveal, .header-slide").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="star-bg-fixed" aria-hidden="true" />
      <div className="star-tint min-h-screen relative">
        <Navigation />
        <HeroSection />
        <ConceptSection />
        <LaunchEventSection />
        <ExperienceSection />
        <SectionDivider size="sm" />
        <DestinationsIntroSection />
        <SectionDivider size="sm" />
        <DestinationsGroup />
        <TheDestinationSection />
        <EntertainmentSection />
        <ArtistsSection />
        <SocialsSection />
        <GiftSection />
        <PersonalAssistantSection />
        <AITechnologySection />
        <AccessSection />
        <FinalCTASection />
        <Footer />
        <AudioToggle />
        <ChatbotWidget />
      </div>
    </>
  );
}
